// TreatmentMessagesManager — manage automated PRE / POST messages per
// service. Owner sets a rule (e.g. "send SMS 24 hours before Botox") and
// the pre-post-cron Edge Function dispatches them on schedule.
import { useEffect, useState } from "react";
import { supabase } from "./supabase";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  purple:"#7c3aed",
  text:"#1a1a2e", muted:"#6b7280", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";
const inp = {width:"100%",padding:"10px 12px",background:"#fff",border:"1px solid "+C.border,borderRadius:8,fontSize:14,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"};

const DEFAULT_PRE_SMS = `Hi {{first_name}}! Quick reminder: your {{service}} appointment is tomorrow at {{time}}. Please come with a clean face & let us know of any updates. — Nova Med Spa`;
const DEFAULT_POST_SMS = `Hi {{first_name}}! Hope you loved your {{service}} today. Reminder: avoid heavy exercise/heat for 24h. Reach out anytime — Nova Med Spa 💛`;
const DEFAULT_PRE_EMAIL = `Hi {{first_name}},\n\nThis is a friendly reminder that your {{service}} appointment with {{provider}} is on {{date}} at {{time}}.\n\nPlease arrive 10 minutes early with a clean face.\n\nSee you soon!\nNova Med Spa`;
const DEFAULT_POST_EMAIL = `Hi {{first_name}},\n\nThank you for visiting us for your {{service}} on {{date}}!\n\nPlease follow these aftercare instructions:\n• Avoid heavy exercise for 24 hours\n• No heat (sauna, hot showers) for 24 hours\n• Don't massage the treated area\n\nWe look forward to seeing you again!\nNova Med Spa`;

const VARIABLES = ["{{first_name}}", "{{full_name}}", "{{service}}", "{{provider}}", "{{date}}", "{{time}}"];

export default function TreatmentMessagesManager() {
  const [services, setServices] = useState([]);
  const [rules, setRules] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendRule, setSendRule] = useState(null); // rule being sent manually

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    const [svc, rls] = await Promise.all([
      supabase.from("services").select("id,name,category,active").eq("active", true).order("name"),
      supabase.from("treatment_messages").select("*, services(name,category)").order("type").order("offset_hours"),
    ]);
    setServices(svc.data || []);
    setRules(rls.data || []);
    setLoading(false);
  };

  const addNew = (type) => setEditing({
    type,
    channel: "sms",
    offset_hours: type === "pre" ? 24 : 24,
    // ★ Default new PRE rules to "on_booking" so the owner gets the instant
    //   experience she keeps asking for ("I booked an appt and didn't get a
    //   pre-care email"). POST rules stay time_based — there's no sense in
    //   firing aftercare at the moment of booking.
    trigger_event: type === "pre" ? "on_booking" : "time_based",
    service_id: "",
    subject: "",
    body: type === "pre" ? DEFAULT_PRE_SMS : DEFAULT_POST_SMS,
    active: true,
  });

  const save = async () => {
    if (!editing.service_id) { alert("Pick a service"); return; }
    if (!editing.body) { alert("Body required"); return; }
    const row = {
      service_id: +editing.service_id,
      type: editing.type,
      channel: editing.channel,
      offset_hours: +editing.offset_hours,
      trigger_event: editing.trigger_event || "time_based",
      subject: editing.subject || null,
      body: editing.body,
      active: editing.active,
      updated_at: new Date().toISOString(),
    };
    // ★ Defensive write — the `trigger_event` column was added by a
    //   migration on 2026-06-03. If the migration hasn't been applied to
    //   this DB yet, Postgres returns PGRST204 "could not find column".
    //   Detect that and retry without the field so saves never fail in
    //   the meantime. The rule still saves; it just behaves as
    //   time_based until the column lands.
    const writeRow = async (r) => {
      if (editing.id) return await supabase.from("treatment_messages").update(r).eq("id", editing.id);
      return await supabase.from("treatment_messages").insert([r]);
    };
    let { error } = await writeRow(row);
    if (error && /trigger_event/i.test(error.message || "")) {
      console.warn("trigger_event column missing — saving without it. Run the 20260603 migration to enable instant-on-booking.");
      const { trigger_event, ...rest } = row;
      ({ error } = await writeRow(rest));
      if (!error) {
        alert("Saved. ⚠ The 'fire on booking' setting needs a one-time DB migration before it works — ping dev.");
      }
    }
    if (error) {
      alert("Save failed: " + error.message);
      return;
    }
    setEditing(null);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this rule? Any pending pre/post messages tied to it won't be sent.")) return;
    await supabase.from("treatment_messages").delete().eq("id", id);
    load();
  };

  const toggleActive = async (r) => {
    await supabase.from("treatment_messages").update({ active: !r.active }).eq("id", r.id);
    load();
  };

  const insertVar = (v) => setEditing(e => ({ ...e, body: (e.body || "") + " " + v }));

  // When channel switches between sms/email/both, swap to a sensible default
  // template if the box still holds the OLD default. Don't clobber user edits.
  const updateChannel = (ch) => {
    setEditing(e => {
      const isPre = e.type === "pre";
      const oldDefaults = [DEFAULT_PRE_SMS, DEFAULT_POST_SMS, DEFAULT_PRE_EMAIL, DEFAULT_POST_EMAIL];
      let body = e.body;
      if (oldDefaults.includes(body)) {
        if (ch === "email") body = isPre ? DEFAULT_PRE_EMAIL : DEFAULT_POST_EMAIL;
        else                body = isPre ? DEFAULT_PRE_SMS   : DEFAULT_POST_SMS;
      }
      return { ...e, channel: ch, body };
    });
  };

  if (loading) return <div style={{padding:30,textAlign:"center",color:C.muted}}>Loading…</div>;

  const preRules = rules.filter(r => r.type === "pre");
  const postRules = rules.filter(r => r.type === "post");

  return (
    <div>
      <div style={{marginBottom:20}}>
        <div style={{fontSize:22,fontWeight:800,color:C.gold,fontFamily:S}}>Pre &amp; Post Treatment Messages</div>
        <div style={{fontSize:12,color:C.muted,marginTop:2}}>Auto-send SMS/email before each appointment (prep instructions) and after (aftercare). Configured per service.</div>
      </div>

      {[
        ["pre",  "⏰ Pre-Treatment",  preRules,  "Before the appointment — prep instructions, reminders"],
        ["post", "💌 Post-Treatment", postRules, "After the appointment — aftercare, follow-up"],
      ].map(([type, label, list, desc]) => (
        <div key={type} style={{marginBottom:24}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,flexWrap:"wrap",gap:8}}>
            <div>
              <div style={{fontSize:15,fontWeight:800,color:C.text,fontFamily:S}}>{label}</div>
              <div style={{fontSize:11,color:C.muted}}>{desc}</div>
            </div>
            <button onClick={() => addNew(type)} style={{padding:"8px 14px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>+ Add {type === "pre" ? "Pre" : "Post"} Rule</button>
          </div>

          {list.length === 0 ? (
            <div style={{padding:18,background:C.light,border:"1px dashed "+C.border,borderRadius:8,textAlign:"center",fontSize:12,color:C.muted}}>
              No {type}-treatment rules yet. Click "+ Add {type === "pre" ? "Pre" : "Post"} Rule" to set one up.
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {list.map(r => {
                const dirLabel = r.type === "pre" ? "before" : "after";
                const isOnBooking = (r.trigger_event || "time_based") === "on_booking";
                const timing = isOnBooking
                  ? "instant on booking"
                  : (r.offset_hours >= 24 && r.offset_hours % 24 === 0
                      ? `${r.offset_hours / 24} day${r.offset_hours === 24 ? "" : "s"} ${dirLabel}`
                      : `${r.offset_hours} hour${r.offset_hours === 1 ? "" : "s"} ${dirLabel}`);
                return (
                  <div key={r.id} style={{padding:"12px 14px",background:"#fff",border:"1px solid "+C.border,borderRadius:9,opacity:r.active?1:0.55,borderLeft:"3px solid "+(r.type==="pre"?C.blue:C.purple)}}>
                    <div style={{display:"flex",justifyContent:"space-between",gap:8,marginBottom:6,flexWrap:"wrap"}}>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                        <div style={{fontSize:13,fontWeight:700,color:C.text}}>{r.services?.name || "(deleted service)"}</div>
                        <span style={{fontSize:10,padding:"2px 8px",background:r.channel==="email"?C.greenL:r.channel==="sms"?C.blueL:C.goldBg,color:r.channel==="email"?C.green:r.channel==="sms"?C.blue:C.gold,borderRadius:50,fontWeight:700,textTransform:"uppercase"}}>{r.channel}</span>
                        <span style={{fontSize:10,padding:"2px 8px",background:isOnBooking?C.greenL:C.light,color:isOnBooking?C.green:C.muted,borderRadius:50,fontWeight:700}}>{timing}</span>
                        {!r.active && <span style={{fontSize:10,padding:"2px 8px",background:C.muted,color:"#fff",borderRadius:50,fontWeight:700}}>OFF</span>}
                      </div>
                      <div style={{display:"flex",gap:4}}>
                        <button onClick={() => setSendRule(r)} title="Send this email now to any address" style={{padding:"6px 10px",background:C.blueL,color:C.blue,border:"1px solid "+C.blue+"40",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>✉️ Send manually</button>
                        <button onClick={() => setEditing(r)} style={{padding:"6px 10px",background:C.goldBg,color:C.gold,border:"1px solid "+C.borderGold,borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>Edit</button>
                        <button onClick={() => toggleActive(r)} style={{padding:"6px 10px",background:r.active?C.light:C.greenL,color:r.active?C.muted:C.green,border:"1px solid "+(r.active?C.border:C.green+"40"),borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>{r.active?"Disable":"Enable"}</button>
                        <button onClick={() => remove(r.id)} style={{padding:"6px 8px",background:C.redL,color:C.red,border:"1px solid "+C.red+"30",borderRadius:6,fontSize:11,cursor:"pointer",fontFamily:F}}>🗑</button>
                      </div>
                    </div>
                    <div style={{fontSize:11,color:C.muted,whiteSpace:"pre-wrap",fontFamily:"monospace",background:C.light,padding:"8px 10px",borderRadius:6}}>{(r.body || "").slice(0, 200)}{r.body?.length > 200 ? "…" : ""}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}

      {editing && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:"10px",backdropFilter:"blur(4px)"}}>
          <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:600,padding:"clamp(14px,4vw,24px)",maxHeight:"94vh",overflowY:"auto",position:"relative"}}>
            <button onClick={()=>setEditing(null)} style={{position:"absolute",top:12,right:12,background:C.light,border:"none",borderRadius:7,padding:"4px 10px",cursor:"pointer",fontSize:16,color:C.muted}}>×</button>
            <div style={{fontSize:18,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:14}}>
              {editing.id ? "Edit" : "New"} {editing.type === "pre" ? "Pre" : "Post"}-Treatment Rule
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div>
                <label style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:"uppercase",display:"block",marginBottom:5}}>Service *</label>
                <select value={editing.service_id} onChange={e=>setEditing({...editing,service_id:e.target.value})} style={inp}>
                  <option value="">— Select a service —</option>
                  {services.map(s => <option key={s.id} value={s.id}>{s.name} {s.category ? `(${s.category})` : ""}</option>)}
                </select>
              </div>
              <div>
                <label style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:"uppercase",display:"block",marginBottom:5}}>When to send</label>
                <div style={{display:"flex",gap:4,background:C.light,padding:3,borderRadius:8}}>
                  <button
                    onClick={() => setEditing({...editing, trigger_event: "on_booking"})}
                    style={{flex:1,padding:"10px 8px",background:(editing.trigger_event||"time_based")==="on_booking"?"#fff":"transparent",color:(editing.trigger_event||"time_based")==="on_booking"?C.green:C.muted,border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F,lineHeight:1.3}}
                  >
                    ⚡ Instantly when booked
                  </button>
                  <button
                    onClick={() => setEditing({...editing, trigger_event: "time_based"})}
                    style={{flex:1,padding:"10px 8px",background:(editing.trigger_event||"time_based")==="time_based"?"#fff":"transparent",color:(editing.trigger_event||"time_based")==="time_based"?C.gold:C.muted,border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F,lineHeight:1.3}}
                  >
                    ⏰ X hours {editing.type==="pre"?"before":"after"} appt
                  </button>
                </div>
                <div style={{fontSize:10,color:C.muted,marginTop:4}}>
                  {(editing.trigger_event||"time_based")==="on_booking"
                    ? "Sends within seconds of the appointment being booked. Best for pre-care since clients often book same-day."
                    : "Sends X hours relative to the appointment time. Best for traditional reminders."}
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div>
                  <label style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:"uppercase",display:"block",marginBottom:5}}>Channel</label>
                  <div style={{display:"flex",gap:4,background:C.light,padding:3,borderRadius:8}}>
                    {["sms","email","both"].map(ch => (
                      <button key={ch} onClick={()=>updateChannel(ch)} style={{flex:1,padding:"8px",background:editing.channel===ch?"#fff":"transparent",color:editing.channel===ch?C.gold:C.muted,border:"none",borderRadius:6,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F,textTransform:"uppercase"}}>{ch}</button>
                    ))}
                  </div>
                </div>
                {(editing.trigger_event || "time_based") === "time_based" ? (
                  <div>
                    <label style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:"uppercase",display:"block",marginBottom:5}}>
                      {editing.type === "pre" ? "Hours BEFORE appt" : "Hours AFTER appt"}
                    </label>
                    <select value={editing.offset_hours} onChange={e=>setEditing({...editing,offset_hours:+e.target.value})} style={inp}>
                      <option value={1}>1 hour</option>
                      <option value={2}>2 hours</option>
                      <option value={3}>3 hours</option>
                      <option value={6}>6 hours</option>
                      <option value={12}>12 hours</option>
                      <option value={24}>24 hours (1 day)</option>
                      <option value={48}>48 hours (2 days)</option>
                      <option value={72}>72 hours (3 days)</option>
                      <option value={168}>1 week</option>
                    </select>
                  </div>
                ) : (
                  <div>
                    <label style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:"uppercase",display:"block",marginBottom:5}}>Timing</label>
                    <div style={{padding:"10px 12px",background:C.greenL,border:"1px solid "+C.green+"40",borderRadius:8,fontSize:12,color:C.green,fontWeight:600}}>
                      ⚡ Fires instantly on booking
                    </div>
                  </div>
                )}
              </div>

              {(editing.channel === "email" || editing.channel === "both") && (
                <div>
                  <label style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:"uppercase",display:"block",marginBottom:5}}>Email Subject</label>
                  <input value={editing.subject || ""} onChange={e=>setEditing({...editing,subject:e.target.value})} placeholder={`e.g. Before your appointment tomorrow`} style={inp}/>
                </div>
              )}

              <div>
                <label style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:"uppercase",display:"block",marginBottom:5}}>
                  Message Body{editing.channel==="both"?" (used for both SMS and email)":""}
                </label>
                <textarea value={editing.body} onChange={e=>setEditing({...editing,body:e.target.value})} rows={editing.channel==="sms"?4:8} placeholder="Type your message…" style={{...inp,fontFamily:"monospace",fontSize:13,resize:"vertical",minHeight:90}}/>
                <div style={{display:"flex",gap:4,marginTop:6,flexWrap:"wrap"}}>
                  <span style={{fontSize:10,color:C.muted,alignSelf:"center"}}>Insert:</span>
                  {VARIABLES.map(v => (
                    <button key={v} onClick={()=>insertVar(v)} style={{padding:"3px 8px",background:C.light,color:C.text,border:"1px solid "+C.border,borderRadius:5,fontSize:10,fontFamily:"monospace",cursor:"pointer"}}>{v}</button>
                  ))}
                </div>
              </div>

              <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:C.text,cursor:"pointer"}}>
                <input type="checkbox" checked={editing.active} onChange={e=>setEditing({...editing,active:e.target.checked})}/>
                Active (rule will fire on the schedule above)
              </label>
            </div>

            <div style={{display:"flex",gap:8,marginTop:18}}>
              <button onClick={()=>setEditing(null)} style={{flex:1,padding:"11px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:8,fontSize:13,cursor:"pointer",fontFamily:F}}>Cancel</button>
              <button onClick={save} style={{flex:2,padding:"11px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F}}>{editing.id ? "Save changes" : "Create rule"}</button>
            </div>
          </div>
        </div>
      )}

      {sendRule && <ManualSendModal rule={sendRule} onClose={() => setSendRule(null)} />}
    </div>
  );
}

// ★ MANUAL SEND (owner request 2026-10-13): (re)send one rule's pre/post
//   email to any address. Typing a client's email fills in her name and her
//   latest appointment for this service, so {{first_name}}, {{date}},
//   {{time}} and {{provider}} come out right. Sent server-side by
//   pre-post-cron (same branded template as the automatic emails).
function ManualSendModal({ rule, onClose }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [provider, setProvider] = useState("");
  const [found, setFound] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const service = rule.services?.name || "your treatment";

  const lookup = async () => {
    const e = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(e)) return;
    const { data: clients } = await supabase.from("clients").select("id,name").ilike("email", e).limit(1);
    const c = clients?.[0];
    if (!c) { setFound("Not a client in Elyon — fill in the name below."); return; }
    setName(c.name || "");
    const { data: appts } = await supabase.from("appointments").select("date,time,prov,treat")
      .eq("cid", c.id).neq("status", "cancelled").order("date", { ascending: false }).limit(20);
    const word = service.split(/[\s—-]+/)[0].toLowerCase();
    const a = (appts || []).find(x => String(x.treat || "").toLowerCase().includes(word)) || (appts || [])[0];
    if (a) { setDate(a.date || ""); setTime(String(a.time || "").slice(0, 5)); setProvider(a.prov || ""); }
    setFound(`Found ${c.name}${a ? ` · using ${a.treat} on ${a.date}` : ""}`);
  };

  const fmtD = (d) => d ? new Date(d + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }) : "";
  const fmtT = (t) => { if (!t) return ""; const [h, m] = t.split(":").map(Number); return `${h % 12 || 12}:${String(m || 0).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`; };
  const ctx = { first_name: name.trim().split(" ")[0] || "there", full_name: name.trim() || "there", service, provider: provider || "our team", date: fmtD(date), time: fmtT(time) };
  const render = (s) => String(s || "").replace(/\{\{(\w+)\}\}/g, (_, k) => ctx[k] ?? "");
  const subject = render(rule.subject || `${rule.type === "pre" ? "Before" : "After"} your ${service} appointment`);
  const preview = render(rule.body);

  const send = async () => {
    setSending(true); setResult(null);
    const { data, error } = await supabase.functions.invoke("pre-post-cron", {
      body: { manual: { rule_id: rule.id, to_email: email.trim(), full_name: name.trim(), date, time, provider } },
    });
    let msg = data?.error || error?.message;
    try { if (error?.context?.json) { const j = await error.context.json(); msg = j?.error || msg; } } catch (_) {}
    setResult(data?.ok ? { ok: true, text: `Sent to ${data.to}` } : { ok: false, text: msg || "Send failed" });
    setSending(false);
  };

  const lbl = { fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5, display: "block" };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, padding: 22, width: "100%", maxWidth: 560, maxHeight: "90vh", overflowY: "auto", position: "relative", fontFamily: F }}>
        <button onClick={onClose} style={{ position: "absolute", top: 12, right: 12, background: C.light, border: "none", borderRadius: 7, padding: "4px 10px", cursor: "pointer", fontSize: 16, color: C.muted }}>×</button>
        <div style={{ fontSize: 18, fontWeight: 800, color: C.gold, fontFamily: S }}>✉️ Send {rule.type === "pre" ? "pre-treatment" : "aftercare"} email</div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>{service}</div>

        <label style={lbl}>Email address</label>
        <input value={email} onChange={e => { setEmail(e.target.value); setFound(""); setResult(null); }} onBlur={lookup} placeholder="client@email.com" type="email" autoCapitalize="none" style={inp} />
        {found && <div style={{ fontSize: 11, color: found.startsWith("Found") ? C.green : C.muted, marginTop: 5 }}>{found}</div>}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 12 }}>
          <div style={{ gridColumn: "1/-1" }}><label style={lbl}>Client name</label><input value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith" style={inp} /></div>
          <div><label style={lbl}>Appointment date</label><input type="date" value={date} onChange={e => setDate(e.target.value)} style={inp} /></div>
          <div><label style={lbl}>Time</label><input type="time" value={time} onChange={e => setTime(e.target.value)} style={inp} /></div>
          <div style={{ gridColumn: "1/-1" }}><label style={lbl}>Provider</label><input value={provider} onChange={e => setProvider(e.target.value)} placeholder="Olga Rafailova" style={inp} /></div>
        </div>

        <label style={{ ...lbl, marginTop: 14 }}>Preview</label>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 6 }}>Subject: {subject}</div>
        <div style={{ fontSize: 12, color: C.text, whiteSpace: "pre-wrap", background: C.light, padding: "10px 12px", borderRadius: 8, maxHeight: 220, overflowY: "auto" }}>
          {preview.startsWith("<") ? "(HTML email — sent with the same design as the automatic one)" : preview}
        </div>

        {result && <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 8, fontSize: 13, fontWeight: 700, background: result.ok ? C.greenL : C.redL, color: result.ok ? C.green : C.red }}>{result.ok ? "✓ " : "⚠ "}{result.text}</div>}

        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "11px", background: C.light, color: C.muted, border: "1px solid " + C.border, borderRadius: 8, fontSize: 13, cursor: "pointer", fontFamily: F }}>Close</button>
          <button onClick={send} disabled={sending || !email.trim()} style={{ flex: 2, padding: "11px", background: sending || !email.trim() ? "#ccc" : "linear-gradient(135deg," + C.gold + "," + C.goldD + ")", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: sending || !email.trim() ? "default" : "pointer", fontFamily: F }}>
            {sending ? "Sending…" : result?.ok ? "Send again" : "Send email"}
          </button>
        </div>
      </div>
    </div>
  );
}
